import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import useQuery from '../../modules/react/hooks/useQuery';
import useMutation from '../../modules/react/hooks/useMutation';
import Component from '../components/HostnameCertSettings';

const GET_HOSTNAMES = gql`
  query GetHostnames {
    hostnames {
      ...HostnameCertSettingsHostname
    }
  }

  ${Component.fragments.hostname}
`;

const UPDATE_HOSTNAMES = gql`
  mutation UpdateHostnames($primary: DomainUpdateInput, $secondaries: [DomainUpdateInput]) {
    domainUpdate(primary: $primary, secondaries: $secondaries) {
      success
      userErrors {
        ...HostnameCertSettingsUserError
      }
    }
  }

  ${Component.fragments.userError}
`;

const CREATE_ROBOCERT = gql`
  mutation CreateRobocert($hostname: String!) {
    createRobocert(hostname: $hostname) {
      userErrors {
        ...HostnameCertSettingsUserError
      }
    }
  }

  ${Component.fragments.userError}
`;

const propTypes = {
  router: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  setAlert: PropTypes.func.isRequired,
};

const defaultProps = {};

const HostnameCertSettings = ({ setAlert }) => {
  const {
    data: getHostnamesData,
    loading: getHostnamesLoading,
    refetch,
  } = useQuery(GET_HOSTNAMES);

  const [
    hostnamesUpdate,
    {
      data: updatedHostnamesData,
      errors: hostnamesUpdateErrors,
      loading: hostnamesUpdateLoading,
    },
  ] = useMutation(UPDATE_HOSTNAMES, { refetchQueries: ['GetHostnames'] });

  const [createRobocert] = useMutation(CREATE_ROBOCERT, { refetchQueries: ['GetHostnames'] });

  const [userErrors, setUserErrors] = useState([]);
  const [submitErrors, setSubmitErrors] = useState([]);

  useEffect(() => {
    const errorsExist = hostnamesUpdateErrors?.length || userErrors?.length;
    if (errorsExist) {
      setSubmitErrors([
        ...(userErrors || []),
        ...(hostnamesUpdateErrors || []),
      ]);
    } else {
      setSubmitErrors([]);
    }
  }, [hostnamesUpdateErrors, userErrors]);

  if (!getHostnamesData) {
    return (
      <React.Fragment>
        <p>This page is blank because the Hostnames Data is not available.</p>
      </React.Fragment>
    );
  }

  const onSubmit = async (hostnames) => {
    const { primaryHostname, secondaryHostnames } = hostnames;

    // Allowing blank hostnames here because Implementation has historically used them as a
    // placeholder when building new sites
    const primary = {
      id: primaryHostname.id,
      hostname: primaryHostname.hostname ?? '',
      sslType: primaryHostname.sslType,
    };

    const secondaries = [];
    secondaryHostnames.forEach((hostname) => {
      if (hostname.hostname) {
        secondaries.push({
          id: hostname.id,
          hostname: hostname.hostname,
          sslType: hostname.sslType,
        });
      }
    });

    const { data: { domainUpdate: { userErrors: errors } } } = await hostnamesUpdate({
      variables: {
        primary,
        secondaries,
      },
    });

    if (errors) {
      setUserErrors(errors);
    }
  };

  const onRequestSSL = async ({ hostname }) => {
    createRobocert({ variables: { hostname } });
  };

  const { hostnames } = getHostnamesData;
  let primaryHostname;
  const secondaryHostnames = [];

  hostnames.forEach((hostname) => {
    // eslint-disable-next-line no-param-reassign
    hostname.hostname = hostname.hostname.replace(/^(www\.)/, '');
    if (hostname.primary) {
      primaryHostname = hostname;
    } else {
      secondaryHostnames.push(hostname);
    }
  });

  const transformedHostnames = {
    primaryHostname,
    secondaryHostnames,
  };

  return (
    <Component
      hostnames={transformedHostnames}
      onSubmit={onSubmit}
      onRequestSSL={onRequestSSL}
      loading={getHostnamesLoading}
      refetchHostnames={refetch}
      submitting={hostnamesUpdateLoading}
      submitErrors={submitErrors}
      submitted={!!updatedHostnamesData}
      setAlert={setAlert}
    />
  );
};

HostnameCertSettings.propTypes = propTypes;
HostnameCertSettings.defaultProps = defaultProps;

export default HostnameCertSettings;
